
import { Logger }               from '@/util/logger';
import { Buffer }               from '@/loader/e.m.c.a.404.types';

import { JSONLoader }           from '@/loader/j.s.o.n.loader';
import {
  ParseFailureEvent,
  ParseSuccessEvent,
  ParsingStartEvent
}                               from '@/loader/loader.events';
import {basename, errorAsText} from '@/util/util';

/**
 *
 * @class MovementMetaBuilder
 *
 * @brief This helper uses incremental reading (event based) of the simulator train movement
 * output files to be able to pick out just the meta information.  So we don't have to have
 * a full understanding of the entire movement file, nor do we have to actually parse and load
 * the whole thing (some of them can be quite large).  This is essentially the 'jq' linux
 * command, but in TypeScript :)  Here we are yanking out the 'job_detail' field and the 'extra'
 * field.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      September 2024
 *
 */
export class MovementMetaBuilder {

  /** the file we are loading */
  private file: File | null         = null;

  /** the loader what loads the load. */
  private loader: JSONLoader | null = null;

  /** the default chunk size to use */
  private chunkSize: number         = 256 * 1024;

  /** the most recent error */
  private lastError: string         = '';

  /** the 'job_detail' field extracted from the JSON (content of the movement file) */
  private job_detail: any           = null;

  /** the 'extra' field extracted from the JSON (content of the movement file) */
  private extra: any                = null;

  constructor() {

    /* for now, nothing to do */

  }

  /** what to do when the JSON loading completes successfully. */
  private loadCompleted(resolve: (meta: {job_detail: any, extra: any}) => void): void {

    let builder: MovementMetaBuilder = this;

    Logger.debug(`[MovementMetaBuilder] loadCompleted - JSON parsing has completed.`);

    if (!builder.job_detail) {

      Logger.error(`[MovementMetaBuilder] loadCompleted - job_detail is missing!`);

      return ;
    }

    if (!builder.extra) {

      Logger.error(`[MovementMetaBuilder] loadCompleted - extra is missing!`);

      return ;
    }


    /* do any summary work at the end */





    /* resolve */

    resolve({job_detail: builder.job_detail, extra: builder.extra});
  }

  /** what to do when the JSON loading fails */
  private loadFailed(reject: (error: string) => void): void {

    let builder: MovementMetaBuilder = this;

    Logger.error(`[MovementMetaBuilder] loadFailed - JSON parsing has failed.`);

    /* do any clean up */




    /* reject */

    reject(builder.lastError);

  }

  /** force the file to parse */
  public setFile(file: File): MovementMetaBuilder {

    this.file = file;

    return this;
  }

  /** fetch the current file */
  public getFile(): File|null {

    return this.file;
  }

  /** force the loader */
  public setLoader(loader: JSONLoader): MovementMetaBuilder {

    this.loader = loader;

    return this;
  }

  /** fetch the current JSON loader */
  public getLoader(): JSONLoader|null {

    return this.loader;
  }

  /** force the chunk size */
  public setChunkSize(chunkSize: number): MovementMetaBuilder {

    this.chunkSize = chunkSize;

    return this;
  }

  /** fetch the current chunk size */
  public getChunkSize(): number {

    return this.chunkSize;
  }

  /** build a train movement model! */
  public  build(): Promise<{ job_detail: any, extra: any }> {

    let builder: MovementMetaBuilder = this;

    Logger.debug(`[MovementMetaBuilder] build - configuring...`);

    /* reset the model */

    builder.job_detail = null;
    builder.extra      = null;

    /* sanity check ... */

    if (!builder.file) {

      /* there is no file to parse! */

      return Promise.reject("No JSON file provided.");
    }

    if (!builder.loader) {

      /* there is no loader! */

      return Promise.reject("No JSON loader provided.");
    }

    /* ensure we have the chunk size we want */

    Logger.debug(`[MovementMetaBuilder] build - chunkSize=${builder.chunkSize}`);

    builder.loader.setChunkSize(builder.chunkSize);

    /* register to handle the JSON as it's parsed... */

    Logger.debug(`[MovementMetaBuilder] build - registering parser outputs...`);

    /* handle the job_detail field... */

    builder.loader.on('job_detail', (data: Buffer, index: string|number|undefined) => {

      //Logger.debug(`[MovementMetaBuilder] job_detail [${index}]:`, data);

      /* give the movement model another train ... */

      try {

        builder.job_detail = JSON.parse(data);

      } catch (err) {

        Logger.error(`[MovementMetaBuilder] build - can not parse 'job_detail' field: ` + errorAsText(err));
      }

    });

    /* handle the extra field... */

    builder.loader.on('extra', (data: Buffer, index: string|number|undefined) => {

      //Logger.debug(`[MovementMetaBuilder] job_detail [${index}]:`, data);

      /* give the movement model another train ... */

      try {

        builder.extra = JSON.parse(data);

      } catch (err) {

        Logger.error(`[MovementMetaBuilder] build - can not parse 'job_detail' field: ` + errorAsText(err));
      }

    });

    /* create the promise of a better tomorrow... */

    Logger.debug(`[MovementMetaBuilder] build - creating promise...`);

    return new Promise(
      function(
        resolve: (meta: {job_detail: any, extra: any}) => void,
        reject: (error: string) => void): void {

        /* register for completed/failed/error events ... */

        // @ts-ignore
        builder.loader.onParsingStart((event: ParsingStartEvent): void => {

          Logger.debug(`[MovementMetaBuilder] build - parsing started: `, event);

          /* do any one time init stuff before bulk of processing happens */

        }).onSuccess((event: ParseSuccessEvent): void => {

          Logger.debug(`[MovementMetaBuilder] build - parsing completed: `, event);

          /* everything is awesome! */

          builder.loadCompleted(resolve);

        }).onFailure((event: ParseFailureEvent): void => {

          Logger.debug(`[MovementMetaBuilder] build - parsing failed: `, event);

          /* I have failed you my king */

          builder.lastError = event.summary;

          builder.loadFailed(reject);

        });

        /* trigger the load */

        Logger.debug(`[MovementMetaBuilder] build - triggering...`);

        // @ts-ignore
        if (!builder.loader.load(builder.file)) {

          reject(`Immediate JSON load error.`);
        }

        Logger.debug(`[MovementMetaBuilder] build - triggered.`);

      });

  }

}
